/**
 * Idle-gated flying bats.
 *
 * A fixed, full-screen canvas with a small flock of bats flapping across in
 * mixed directions. Mirrors the snow effect's gating: fades in after a short
 * idle period (or a live preview), clears on input, disabled under
 * reduced-motion, `pointer-events: none`. Color follows the `--bat-fill` theme
 * var so it adapts to light/dark.
 */
import React, { useEffect, useRef } from 'react';

import { Z } from '@shared/constants/zIndex';
import { useMediaQuery } from '@shared/hooks/useMediaQuery';

import { createBats, drawBats } from './bats';
import { useIsIdle } from '../hooks/useIsIdle';

/** Idle delay before the bats appear. */
const BATS_IDLE_MS = 15000;
/** Number of bats in the flock. */
const BAT_COUNT = 8;
/** Fallback fill if the theme var can't be read. */
const FALLBACK_FILL = 'oklch(0.16 0.03 305)';

/** Full-screen idle bats effect. Renders nothing until the user is idle. */
export const BatsOverlay: React.FC = () => {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const idle = useIsIdle(BATS_IDLE_MS, !reducedMotion);
  const active = !reducedMotion && idle;
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!active) return undefined;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;

    const fill = getComputedStyle(canvas).getPropertyValue('--bat-fill').trim() || FALLBACK_FILL;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);
    let bats = createBats(BAT_COUNT, width, height);
    let raf = 0;
    const render = () => {
      drawBats(ctx, bats, width, height, fill);
      raf = requestAnimationFrame(render);
    };
    const onResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
      bats = createBats(BAT_COUNT, width, height);
    };

    window.addEventListener('resize', onResize);
    raf = requestAnimationFrame(render);
    canvas.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: 1000, easing: 'ease-out' });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [active]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      data-testid="bats-overlay"
      className="pointer-events-none fixed inset-0 h-full w-full"
      style={{ zIndex: Z.AMBIENT }}
    />
  );
};
